import type { PaymentRetry } from '@/features/paymentRetries';
import { useCallback, useEffect, useState } from 'react';
import { fetchCustomerPaymentRetries } from '../api/paymentRetries';

/**
 * Hook for fetching payment retries for a specific customer
 */
export const useCustomerPaymentRetries = (shopifyCustomerId: string) => {
  const [paymentRetries, setPaymentRetries] = useState<PaymentRetry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [perPage, setPerPage] = useState(10);

  const fetchData = useCallback(async () => {
    if (!shopifyCustomerId) return;

    await Promise.resolve();
    setLoading(true);
    setError(null);

    try {
      const response = await fetchCustomerPaymentRetries(shopifyCustomerId, page, perPage);
      setPaymentRetries(response.data);
      setTotal(response.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      setPaymentRetries([]);
    } finally {
      setLoading(false);
    }
  }, [shopifyCustomerId, page, perPage]);

  useEffect(() => {
    void Promise.resolve().then(fetchData);
  }, [fetchData]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePerPageChange = (newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  return {
    paymentRetries,
    loading,
    error,
    page,
    total,
    perPage,
    totalPages: Math.ceil(total / perPage),
    handlePageChange,
    handlePerPageChange,
    refetch: fetchData,
  };
};
